import { useState } from 'react';
import {
  AppBar,
  Avatar,
  Box,
  Chip,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import { formatJordan } from '@serb/shared';
import type { LiveStatus } from '../lib/useLive';
import { SAMPLE_DATA } from '../lib/sample';
import { NotificationBell } from '../features/notifications/NotificationBell';
import { ColorModeToggle } from './ColorModeToggle';
import { LiveIndicator } from './LiveIndicator';

type Props = { device: string; live: LiveStatus; onMenu: () => void; onSignOut: () => void };

/** The white bar across the top: search, date range, today's date, live light, alerts, who is signed in */
export function TopBar({ device, live, onMenu, onSignOut }: Props) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const today = formatJordan(new Date(), { hour: undefined, minute: undefined, year: 'numeric' });

  return (
    <AppBar
      position="sticky"
      color="inherit"
      elevation={0}
      sx={{ borderBottom: 1, borderColor: 'divider', pt: 'env(safe-area-inset-top)' }}
    >
      <Toolbar sx={{ gap: { xs: 1, md: 2 }, pr: 'max(16px, env(safe-area-inset-right))' }}>
        <IconButton edge="start" aria-label="Open menu" onClick={onMenu} sx={{ display: { md: 'none' } }}>
          <MenuIcon />
        </IconButton>
        <TextField
          placeholder="Search shipments, customers, agents, quotations…"
          sx={{ maxWidth: 520, display: { xs: 'none', sm: 'block' } }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
        <Box sx={{ flex: 1 }} />
        {SAMPLE_DATA && <Chip size="small" color="warning" variant="outlined" label="Sample data" />}
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ display: { xs: 'none', md: 'block' }, whiteSpace: 'nowrap' }}
        >
          {today}
        </Typography>
        <ColorModeToggle />
        <LiveIndicator status={live} />
        <NotificationBell />
        <IconButton aria-label="Account" onClick={(e) => setAnchor(e.currentTarget)} sx={{ p: 0.5 }}>
          <Avatar sx={{ width: 34, height: 34, bgcolor: 'primary.main', fontSize: 15 }}>I</Avatar>
        </IconButton>
        <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
          <MenuItem disabled>Signed in on {device}</MenuItem>
          <MenuItem onClick={onSignOut}>Sign out</MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
}
